import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, personSchema, webPageSchema } from "@/lib/seo";
import { AlsoOnShrinkopedia } from "@/components/AlsoOnShrinkopedia";
import { withNetworkUtm } from "@/lib/network";

export default function MedicalReviewBoard() {
  const page = webPageSchema({
    path: "/medical-review-board",
    title: "Medical Review Board & Reviewers | Shariq Refai, MD, MBA",
    description:
      "The medical review board for shariqrefai.com: who reviews clinical content before publication, how reviewers are selected, and the standards they apply.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Medical Review Board", path: "/medical-review-board" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Medical Review Board & Reviewers | Shariq Refai, MD, MBA"
        description="The medical review board for shariqrefai.com: who reviews clinical content before publication, how reviewers are selected, and the standards they apply."
        url="/medical-review-board"
        schema={[
          page,
          personSchema,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Medical Review Board", path: "/medical-review-board" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Medical Review Board</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Medical Review Board
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            Who reviews the clinical content on this site, what they're
            looking for, and how that review process is kept honest.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Effective date: May 25, 2026.</strong>
          </p>

          <p>
            shariqrefai.com is a physician-led educational and publishing
            platform. Content that touches on mental health, psychiatry,
            medication, diagnosis, or clinical reasoning is subject to
            medical review before publication and is re-reviewed when the
            underlying evidence base changes. This page names who performs
            that review, what standards they apply, and how the process is
            documented.
          </p>

          <h2>Editor in Chief and Medical Reviewer</h2>
          <h3>Shariq Refai, MD, MBA</h3>
          <p>
            Dr. Refai is a board-eligible psychiatrist, founder of
            shrinkMD, and the author of the books published through
            shrinkMD Publishing. He serves as editor in chief and primary
            medical reviewer for this website. In that role he is
            responsible for the final clinical accuracy, framing, and
            editorial judgment of every page on shariqrefai.com,
            including pages on which he had AI assistance for drafting,
            structural revision, or copyediting.
          </p>
          <p>
            See the <Link href="/about">About</Link> page for full
            credentials and the <Link href="/disclosures">Disclosures</Link>{" "}
            page for his ownership and editorial relationships across
            shrinkMD, shrinQ, Unstuck, and shrinkMD Publishing.
          </p>

          <h2>Current Reviewer Roster</h2>
          <p>
            As of the effective date above, the medical review board
            consists of Dr. Refai as editor in chief and sole standing
            reviewer. As the network grows, the board will be expanded
            with additional licensed clinicians whose names, credentials,
            and disclosures will be listed publicly on this page. Until
            that expansion is complete, this page should be read as a
            statement of the current single-reviewer structure, not a
            claim of a multi-clinician board.
          </p>

          <h2>What Gets Reviewed</h2>
          <p>
            Content reviewed before publication or significant revision
            includes:
          </p>
          <ul>
            <li>Essays and perspectives that touch on psychiatric or psychological topics.</li>
            <li>Frameworks, models, and explanatory diagrams used to describe mental health concepts.</li>
            <li>Glossary entries and FAQ answers that describe symptoms, conditions, or treatments.</li>
            <li>Books published through shrinkMD Publishing and any companion materials linked from this site.</li>
            <li>Quizzes, screening prompts, or interactive tools (none of which currently exist on this site; see <Link href="/community-guidelines">Community Guidelines</Link> for the standard that will apply when they do).</li>
            <li>Any content drafted with substantial AI assistance, per the <Link href="/ai-policy">AI Use Policy</Link>.</li>
          </ul>
          <p>
            Content that does not require medical review includes purely
            administrative pages (privacy, terms, contact mechanics) and
            promotional or biographical pages that do not make clinical
            claims.
          </p>

          <h2>Review Standards</h2>
          <p>
            Medical review on this site evaluates content against the
            following standards:
          </p>
          <ul>
            <li>
              <strong>Evidence alignment.</strong> Clinical claims are
              checked against the evidence hierarchy described on the{" "}
              <Link href="/evidence">Evidence Methodology</Link> page,
              with stronger evidence given more weight than expert
              opinion.
            </li>
            <li>
              <strong>Calibrated language.</strong> Certainty in the
              writing should not exceed certainty in the evidence.
              Reviewers flag overclaiming, false equivalence, and
              causal language where only association is supported.
            </li>
            <li>
              <strong>No individualized advice.</strong> Reviewers ensure
              content reads as general educational commentary, not as
              individualized diagnosis or treatment of any reader, in
              accordance with the{" "}
              <Link href="/disclaimer">Educational Disclaimer</Link>.
            </li>
            <li>
              <strong>Safety framing.</strong> Content involving suicide,
              self-harm, eating disorders, substance use, psychosis, or
              other higher-risk topics is reviewed for appropriate safety
              language, crisis resources, and avoidance of harmful
              detail.
            </li>
            <li>
              <strong>Conflict of interest.</strong> Where content
              relates to shrinkMD, shrinQ, Unstuck, shrinkMD Publishing,
              or any other Refai-owned venture, the relationship is
              disclosed inline or on the <Link href="/disclosures">Disclosures</Link> page.
            </li>
            <li>
              <strong>Source transparency.</strong> Substantive clinical
              claims are accompanied by sources where practical, and
              reviewers flag content that depends on unsourced
              assertions.
            </li>
          </ul>

          <p>
            These standards align with the network's broader editorial
            governance. Shrinkopedia's{" "}
            <a
              href={withNetworkUtm("https://shrinkopedia.com/editorial-team/", "medical_review_board_editorial_team")}
              target="_blank"
              rel="noopener noreferrer"
            >
              editorial team page
            </a>{" "}
            documents how reviewers are named across the network's editorial
            properties, including my reviewer-of-record role there.
          </p>

          <h2>How AI Fits Into Review</h2>
          <p>
            AI tools may be used for drafting, organization,
            summarization, grammar, and editorial workflow. AI tools do
            not independently publish content. Medical content undergoes
            human review and editorial oversight before publication. AI
            does not replace human review, medical judgment, diagnosis,
            or treatment decisions. See the{" "}
            <Link href="/ai-policy">AI Use Policy</Link> for the full
            position.
          </p>

          <h2>Re-Review and Corrections</h2>
          <p>
            Reviewed content is not frozen. Pages are re-reviewed when:
          </p>
          <ul>
            <li>New high-quality evidence (such as a major meta-analysis, systematic review, or regulatory action) changes the picture.</li>
            <li>Clinical guidelines from a recognized body are updated in a way that affects the recommendation or explanation.</li>
            <li>A reader, clinician, or researcher submits a correction request via the <Link href="/corrections">Corrections Policy</Link> that, on review, is substantiated.</li>
            <li>A factual or framing error is identified internally.</li>
          </ul>
          <p>
            Material updates to reviewed content are noted on the page,
            in keeping with the corrections and versioning standards on
            the <Link href="/corrections">Corrections</Link> page.
          </p>

          <h2>Reviewer Independence</h2>
          <p>
            The medical review function on this site is editorially
            independent of advertising, sponsorship, and commercial
            partnerships. shariqrefai.com does not accept paid placement,
            sponsored editorial, or advertorial content. Where Dr. Refai
            has a financial interest in a topic (for example, content
            that references shrinkMD, shrinQ, Unstuck, or his books),
            the interest is disclosed and the review applies the same
            standards as any other piece. See the{" "}
            <Link href="/disclosures">Disclosures</Link> page for the
            full inventory of relationships.
          </p>

          <h2>How to Reach the Medical Review Board</h2>
          <p>
            Questions about a specific clinical claim, requests for the
            reviewer of record on a given page, or formal correction
            submissions can be sent to{" "}
            <a href="mailto:corrections@shariqrefai.com">
              corrections@shariqrefai.com
            </a>{" "}
            or via the <Link href="/contact">contact page</Link>.
          </p>
          <p>
            For media inquiries about the review process itself, please
            use the <Link href="/media-inquiries">Media Inquiries</Link> page
            rather than the corrections inbox.
          </p>

          <h2>Crisis Resources</h2>
          <p>
            This page describes how content is reviewed. It is not
            clinical care. If you are in crisis or feel unsafe, call or
            text <a href="tel:988">988</a> in the United States, call{" "}
            <a href="tel:911">911</a>, or go to the nearest emergency
            room. Outside the United States, contact your local emergency
            services.
          </p>
        </div>
      </article>
    
      <section className="bg-background py-16 md:py-20 px-4 md:px-8 border-t border-border">
        <div className="container mx-auto max-w-3xl">
          <AlsoOnShrinkopedia slug="medical-review-board" variant="credentialing" />
        </div>
      </section>
    </SiteLayout>
  );
}
